import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import { useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import AssignChecklist from '../shared/AssignChecklist';

interface AlertProps {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
}

/** Rutas asignadas a un usuario, sede por sede. */
const UserRoutes: React.FC<AlertProps> = () => {
  const t = useT();
  const notify = useNotify();
  const { id } = useParams();
  const [selectedRoutes, setSelectedRoutes] = useState<number[]>([]);
  const [unselectedRoutes, setUnselectedRoutes] = useState<number[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [offices, setOffices] = useState<Office[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  setPageTitle('users.assignRoutesTitle');
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`users/${id}/offices`);
        setOffices(Array.isArray(data?.offices) ? data.offices : []);
      } catch (err) {
        notify.error(t('common.loadError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadRoutes = async (selectedOfficeId: string) => {
    setOfficeId(selectedOfficeId);
    setLoading(true);
    try {
      const { data } = await axios.get(`users/${id}/offices/${selectedOfficeId}/routes`);
      setRoutes(Array.isArray(data?.routes) ? data.routes : []);
      setSelectedRoutes((Array.isArray(data?.selectedRoutes) ? data.selectedRoutes : []).map((route: Route) => route.id));
      setUnselectedRoutes([]);
    } catch (err) {
      notify.error(t('common.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (routeId: number, checked: boolean) => {
    setSelectedRoutes((prev) => (checked ? [...prev, routeId] : prev.filter((r) => r !== routeId)));
    setUnselectedRoutes((prev) => (checked ? prev.filter((r) => r !== routeId) : prev.includes(routeId) ? prev : [...prev, routeId]));
  };

  const toggleAll = (checked: boolean) => {
    setSelectedRoutes(checked ? routes.map((r) => r.id) : []);
    setUnselectedRoutes(checked ? [] : routes.map((r) => r.id));
  };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`/users/${id}/routes`, {
        routeId: selectedRoutes,
        setUnselectedRoute: unselectedRoutes,
        userId: id,
        officeId,
      });
      notify.success(t('users.assignmentSavedOk'));
    } catch (err) {
      notify.error(t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AssignChecklist
      tourPrefix="user-routes"
      title={t('users.assignRoutesTitle')}
      description={t('users.assignRoutesHint')}
      breadcrumbs={[{ label: t('users.title'), href: '#/admin/users' }, { label: t('users.assignRoutesTitle') }]}
      filters={
        <TextField
          id="user-routes-office"
          data-tour="user-routes-office"
          select
          size="small"
          label={t('users.office')}
          value={offices.length ? officeId : ''}
          onChange={(e) => loadRoutes(e.target.value)}
          fullWidth
        >
          {offices.map((office) => (
            <MenuItem key={office.id} value={String(office.id)}>
              {office.name}
            </MenuItem>
          ))}
        </TextField>
      }
      emptyTitle={officeId ? t('users.noLinkedRecords') : t('users.chooseOfficeFirst')}
      emptyIcon={<AltRouteOutlinedIcon />}
      items={routes.map((r) => ({ id: r.id, name: r.name }))}
      selected={selectedRoutes}
      onToggle={toggle}
      onToggleAll={toggleAll}
      loading={loading}
      cancelTo="/admin/users"
      onSave={submit}
      saving={saving}
      saveDisabled={!officeId}
    />
  );
};

export default UserRoutes;
